"use client";

import { useActionState, useId, useState, type FocusEvent } from "react";
import Image from "next/image";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { handleLogin, type LoginActionState } from "@/app/login/actions";
import { scrollLoginFieldIntoView } from "@/lib/auth/login-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DS_LOGIN_CARD_CLASS } from "@/lib/core/design-system";

const INITIAL_STATE: LoginActionState = {};

function handleFieldFocus(event: FocusEvent<HTMLInputElement>) {
  scrollLoginFieldIntoView(event.currentTarget);
}

export function LoginFormCard() {
  const [state, formAction, isPending] = useActionState(
    handleLogin,
    INITIAL_STATE
  );
  const [showPassword, setShowPassword] = useState(false);
  const formErrorId = useId();

  return (
    <div className={DS_LOGIN_CARD_CLASS}>
      <div className="flex flex-col items-center px-5 pb-2 pt-6 text-center sm:px-8 sm:pt-10">
        <Image
          src="/angels-logo.png"
          alt="Produtora Angels Fashion Models by Bianca Barros"
          width={220}
          height={60}
          priority
          className="h-auto w-auto max-w-[min(180px,70vw)] object-contain sm:max-w-[220px]"
        />
        <h1 className="mt-5 text-xl font-semibold tracking-tight text-primary sm:mt-6 sm:text-2xl">
          Contratos Angels
        </h1>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground sm:mt-2">
          Bem-vindo de volta
        </p>
      </div>

      <div className="px-5 pb-6 pt-3 sm:px-8 sm:pb-10 sm:pt-4">
        <form
          action={formAction}
          className="flex flex-col gap-4"
          aria-label="Entrar no sistema de contratos"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="username">Usuário</Label>
            <Input
              id="username"
              name="username"
              type="text"
              inputMode="text"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Digite seu usuário"
              autoComplete="username"
              enterKeyHint="next"
              required
              disabled={isPending}
              aria-invalid={Boolean(state.error)}
              aria-describedby={state.error ? formErrorId : undefined}
              onFocus={handleFieldFocus}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Digite sua senha"
                autoComplete="current-password"
                enterKeyHint="go"
                required
                disabled={isPending}
                className="pr-11"
                aria-invalid={Boolean(state.error)}
                aria-describedby={state.error ? formErrorId : undefined}
                onFocus={handleFieldFocus}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex min-h-11 min-w-11 items-center justify-center rounded-r-md text-slate-500 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={isPending}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                aria-pressed={showPassword}
              >
                {showPassword ? (
                  <EyeOff className="size-4" aria-hidden />
                ) : (
                  <Eye className="size-4" aria-hidden />
                )}
              </button>
            </div>
          </div>

          {state.error ? (
            <div
              id={formErrorId}
              role="alert"
              aria-live="polite"
              className="rounded-md border border-destructive/25 bg-angels-red-soft px-3 py-2.5 text-center text-sm font-medium leading-relaxed text-destructive"
            >
              {state.error}
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={isPending}
            className="mt-1 min-h-11 w-full touch-manipulation sm:min-h-10"
          >
            {isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Entrando...
              </>
            ) : (
              "Entrar"
            )}
          </Button>
        </form>

        <p className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs leading-relaxed text-muted-foreground sm:mt-6">
          <Lock className="size-3.5 shrink-0 text-angels-purple/70" aria-hidden />
          Acesso restrito · Uso interno
        </p>
      </div>
    </div>
  );
}
